import { Controller } from '@hotwired/stimulus';
import SignaturePad from 'signature_pad';

/**
 * Controlador Stimulus para gestionar firmas digitales con signature_pad
 *
 * Usage:
 * <div data-controller="signature-pad">
 *   <canvas data-signature-pad-target="canvas"></canvas>
 *   <input type="hidden" data-signature-pad-target="input" name="firma_data">
 *   <button data-action="click->signature-pad#clear">Limpiar</button>
 * </div>
 */
export default class extends Controller {
    static targets = ['canvas', 'input', 'preview', 'errorMessage'];

    connect() {
        console.log('Signature Pad Controller conectado');
        // Esperar un momento para que el modal se renderice completamente
        setTimeout(() => {
            this.initializeSignaturePad();
        }, 100);
    }

    disconnect() {
        if (this.signaturePad) {
            this.signaturePad.off();
        }
    }

    initializeSignaturePad() {
        if (!this.hasCanvasTarget) {
            console.error('Canvas target no encontrado');
            return;
        }

        const canvas = this.canvasTarget;
        const container = canvas.parentElement;

        // Asegurar que el canvas tenga dimensiones
        if (!canvas.width || canvas.width === 0) {
            canvas.width = container.offsetWidth || 600;
            canvas.height = 250;
        }

        console.log('Inicializando SignaturePad con dimensiones:', canvas.width, 'x', canvas.height);

        // Inicializar SignaturePad
        this.signaturePad = new SignaturePad(canvas, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 139)', // Azul oscuro para contraste
            minWidth: 1,
            maxWidth: 3,
            throttle: 16, // 60fps
            minDistance: 5,
            velocityFilterWeight: 0.7
        });

        console.log('SignaturePad inicializado correctamente');

        // Listener para detectar cambios
        this.signaturePad.addEventListener('endStroke', () => {
            console.log('Trazo completado');
            this.onSignatureChange();
        });

        // Cargar firma existente si hay datos
        if (this.hasInputTarget && this.inputTarget.value) {
            this.loadSignature(this.inputTarget.value);
        }
    }

    onSignatureChange() {
        if (!this.signaturePad.isEmpty()) {
            this.hideError();

            // Guardar en el input hidden
            if (this.hasInputTarget) {
                const dataURL = this.signaturePad.toDataURL('image/png');
                this.inputTarget.value = dataURL;

                console.log('Firma guardada en input hidden');

                // Dispatch evento personalizado
                this.inputTarget.dispatchEvent(new CustomEvent('signature:changed', {
                    detail: { dataURL },
                    bubbles: true
                }));
            }

            // Actualizar preview si existe
            if (this.hasPreviewTarget) {
                this.updatePreview();
            }
        }
    }

    clear(event) {
        if (event) {
            event.preventDefault();
        }

        console.log('Limpiando firma');

        if (this.signaturePad) {
            this.signaturePad.clear();

            if (this.hasInputTarget) {
                this.inputTarget.value = '';
            }

            if (this.hasPreviewTarget) {
                this.previewTarget.src = '';
                this.previewTarget.style.display = 'none';
            }

            // Dispatch evento
            this.element.dispatchEvent(new CustomEvent('signature:cleared', {
                bubbles: true
            }));
        }
    }

    undo(event) {
        if (event) {
            event.preventDefault();
        }

        console.log('Deshaciendo último trazo');

        if (this.signaturePad) {
            const data = this.signaturePad.toData();
            if (data && data.length > 0) {
                data.pop(); // Remover último trazo
                this.signaturePad.fromData(data);
                this.onSignatureChange();
            }
        }
    }

    loadSignature(dataURL) {
        if (!this.signaturePad || !dataURL) return;

        try {
            this.signaturePad.fromDataURL(dataURL);

            if (this.hasPreviewTarget) {
                this.updatePreview();
            }
        } catch (error) {
            console.error('Error cargando firma:', error);
        }
    }

    updatePreview() {
        if (!this.hasPreviewTarget || !this.signaturePad) return;

        const dataURL = this.signaturePad.toDataURL('image/png');
        this.previewTarget.src = dataURL;
        this.previewTarget.style.display = 'block';
    }

    isEmpty() {
        return !this.signaturePad || this.signaturePad.isEmpty();
    }

    validate() {
        if (this.isEmpty()) {
            this.showError('Debe firmar antes de continuar');
            return false;
        }
        this.hideError();
        return true;
    }

    showError(message) {
        if (this.hasErrorMessageTarget) {
            this.errorMessageTarget.textContent = message;
            this.errorMessageTarget.style.display = 'block';
            this.errorMessageTarget.classList.add('visible');
        }

        // Añadir clase de error al canvas
        if (this.hasCanvasTarget) {
            this.canvasTarget.classList.add('error');
        }
    }

    hideError() {
        if (this.hasErrorMessageTarget) {
            this.errorMessageTarget.style.display = 'none';
            this.errorMessageTarget.classList.remove('visible');
        }

        // Remover clase de error del canvas
        if (this.hasCanvasTarget) {
            this.canvasTarget.classList.remove('error');
        }
    }

    toDataURL(type = 'image/png') {
        return this.signaturePad ? this.signaturePad.toDataURL(type) : null;
    }

    toBlob(callback, type = 'image/png') {
        if (!this.signaturePad) {
            callback(null);
            return;
        }

        this.canvasTarget.toBlob(callback, type);
    }
}

